import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isNil, sortByKey } from '@semios/app-platform-common'
import { InfoMessageBox } from 'components/InfoMessageBox/InfoMessageBox'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { useScreenSize } from 'utils/useScreenSize'
import { ModalDrawerSeasonSettings } from './ModalDrawerSeasonSettings'

export const CumulativeSummaryTable = ({
  points,
  data,
}: {
  points: TPoint[]
  data: routes.Values.Response['points']
}) => {
  const { isWideScreen, screenWidth } = useScreenSize()
  const selectedFieldAssetsStorePropertyId = selectedFieldAssetsStore.getState().property

  const timezone = selectedFieldAssetsStorePropertyId
    ? (fieldAssetStore.getState().properties?.[selectedFieldAssetsStorePropertyId]?.timezone as string)
    : moment.tz.guess()

  const defaultSeasonStartDate =
    Object.values(data ?? {})[0]?.values?.irrigationFlowMeter?.[0]?.metadata?.seasonStartDate ||
    moment.tz(timezone).startOf('year').toISOString()

  const pumpDevicesItems = points
    .map((point) => {
      const flowMeterData = data?.[point.lngLat]?.values?.irrigationFlowMeter?.[0]?.metadata

      return {
        id: point.lngLat,
        label: point.name,
        cumulativeValue7Days: !isNil(flowMeterData?.cumulativeFlowLast7DaysGallons)
          ? unitConverter.flowMeter(flowMeterData?.cumulativeFlowLast7DaysGallons).valueWithSuffix()
          : '-',
        cumulativeValueSeason: !isNil(flowMeterData?.cumulativeFlowSeasonGallons)
          ? unitConverter.flowMeter(flowMeterData?.cumulativeFlowSeasonGallons).valueWithSuffix()
          : '-',
        cumulativeValueYTD: !isNil(flowMeterData?.cumulativeFlowYTDGallons)
          ? unitConverter.flowMeter(flowMeterData?.cumulativeFlowYTDGallons).valueWithSuffix()
          : '-',
      }
    })
    .sort(sortByKey('label'))

  const cellWidth = isWideScreen ? screenWidth / 8 : screenWidth / 4
  const border = `2px solid ${colors.grey200}`
  const padding = '5px 2px'

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: -10,
        marginLeft: -20,
        minWidth: '100%',
      }}
    >
      <div>
        <InfoMessageBox dismissable={true} style={{ padding: '12px 15px', margin: 15 }}>
          <div css={{ fontSize: 16 }}>
            {translate.phrases.banyanApp('Aggregate View not affected by selected date range')}
          </div>
        </InfoMessageBox>
      </div>
      <div
        css={{
          width: cellWidth * 4,
          fontSize: 14,
          marginTop: 10,
        }}
      >
        {/* header */}
        <div>
          <div
            css={{
              'display': 'flex',
              'borderBottom': border,
              '> *': { padding: '10px 2px' },
              'background': colors.grey50,
            }}
          >
            <div css={{ width: cellWidth, marginLeft: 5 }} />
            <div css={{ width: cellWidth, marginLeft: 10 }}>
              {translate.phrases.banyanApp('Last {{count}} Days', { count: 7 })}
            </div>
            <div css={{ width: cellWidth }}>
              <div>{translate.phrases.banyanApp('Season')}</div>
              <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div
                  css={{
                    fontSize: 12,
                    color: colors.grey800,
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 4,
                  }}
                >
                  {translate.phrases.templates('{{labelA}} {{labelB}}', {
                    labelA: translate.phrases.banyanApp('Since'),
                    labelB: translate.dates.format(
                      moment.tz(defaultSeasonStartDate, timezone),
                      'MMM D, YYYY',
                    ),
                  })}
                </div>
                <ModalDrawerSeasonSettings
                  defaultSeasonStartDate={translate.dates.format(
                    moment.tz(defaultSeasonStartDate, timezone),
                    'MM-DD',
                  )}
                  timezone={timezone}
                  flowMeterData={data}
                />
              </div>
            </div>
            <div css={{ width: cellWidth }}>{translate.phrases.banyanApp('YTD')}</div>
          </div>
        </div>
        {/*  body */}
        {pumpDevicesItems.map((item) => (
          <div key={item.id} css={{ 'display': 'flex', 'flexDirection': 'row', '> *': { padding } }}>
            {/* label column */}
            <div
              css={{
                'display': 'flex',
                'flexDirection': 'column',
                'width': cellWidth,
                'borderRight': border,
                'borderBottom': border,
                '> *': { padding },
                'overflowX': 'hidden',
                'maxWidth': isWideScreen ? 'unset' : cellWidth,
              }}
            >
              <span css={{ fontWeight: 400, marginLeft: 5 }}>{item.label}</span>
            </div>
            {/* last 7 days column */}
            <div
              css={{
                'display': 'flex',
                'flexDirection': 'column',
                'justifyContent': 'flex-end',
                'alignItems': 'center',
                'width': cellWidth,
                'borderRight': border,
                'borderBottom': border,
                'fontWeight': 500,
                '> *': { padding },
              }}
            >
              <span>{item.cumulativeValue7Days}</span>
            </div>
            {/* season column */}
            <div
              css={{
                'display': 'flex',
                'flexDirection': 'column',
                'justifyContent': 'flex-end',
                'alignItems': 'center',
                'width': cellWidth,
                'borderRight': border,
                'borderBottom': border,
                'fontWeight': 500,
                '> *': { padding },
              }}
            >
              <span>{item.cumulativeValueSeason}</span>
            </div>
            {/* YTD column */}
            <div
              css={{
                'display': 'flex',
                'flexDirection': 'column',
                'justifyContent': 'flex-end',
                'alignItems': 'center',
                'width': cellWidth,
                'borderRight': border,
                'borderBottom': border,
                'fontWeight': 500,
                '> *': { padding },
              }}
            >
              <span>{item.cumulativeValueYTD}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
